import { Injectable, signal } from '@angular/core';

export type MvaFilter = '' | 'yes' | 'no';
export type TaxFilter = '' | 'q' | 'nq';
export type SortField = 'rate' | 'rating' | 'carrier' | 'min';

/**
 * Shared filter state for the MYGA Rate Sheet (/rate-sheet).
 *
 * The page (RateSheetComponent) and the rail (MygaRateSheetRailComponent) live
 * in different parts of the layout and cannot talk via @Input/@Output. They
 * share these signals through this root singleton — same pattern as other
 * filter state services.
 */
@Injectable({ providedIn: 'root' })
export class MygaRateSheetStateService {
  readonly premium = signal(100_000);
  readonly clientAge = signal<number | null>(null);
  readonly termMin = signal<number | null>(null);
  readonly termMax = signal<number | null>(null);
  readonly minRating = signal(1);
  readonly mvaFilter = signal<MvaFilter>('');
  readonly stateFilter = signal('');
  readonly taxFilter = signal<TaxFilter>('');
  readonly sortField = signal<SortField>('rate');
  readonly showAll = signal(false);

  readonly changeUp = signal(false);
  readonly changeDown = signal(false);
  readonly changeFuture = signal(false);

  readonly resultCount = signal(0);
  readonly carrierCount = signal(0);
  readonly availableTerms = signal<number[]>([]);

  captureFilterState(): Record<string, any> {
    return {
      premium: this.premium(),
      clientAge: this.clientAge(),
      termMin: this.termMin(),
      termMax: this.termMax(),
      minRating: this.minRating(),
      mvaFilter: this.mvaFilter(),
      stateFilter: this.stateFilter(),
      taxFilter: this.taxFilter(),
      sortField: this.sortField(),
      changeUp: this.changeUp(),
      changeDown: this.changeDown(),
      changeFuture: this.changeFuture(),
    };
  }

  applyFilterState(s: Record<string, any>): void {
    if (s['premium'] != null) this.premium.set(s['premium']);
    this.clientAge.set(s['clientAge'] ?? null);
    this.termMin.set(s['termMin'] ?? null);
    this.termMax.set(s['termMax'] ?? null);
    this.minRating.set(s['minRating'] ?? 0);
    this.mvaFilter.set(s['mvaFilter'] ?? '');
    this.stateFilter.set(s['stateFilter'] ?? '');
    this.taxFilter.set(s['taxFilter'] ?? '');
    if (s['sortField']) this.sortField.set(s['sortField']);
    this.changeUp.set(s['changeUp'] ?? false);
    this.changeDown.set(s['changeDown'] ?? false);
    this.changeFuture.set(s['changeFuture'] ?? false);
  }
}
